import React, { useState } from 'react';
import { SubEvent, MasterEvent } from './MasterEventSelector';
import { Button } from '../../ui/Button';
import { Card } from '../../ui/Card';
import { PlusIcon, EditIcon, TrashIcon } from 'lucide-react';
interface SubEventDetailProps {
  subEvent: SubEvent | null;
  masterEvent: MasterEvent;
  onBack: () => void;
  activeTab: 'feed' | 'settings';
}
export function SubEventDetail({
  subEvent,
  masterEvent,
  onBack,
  activeTab
}: SubEventDetailProps) {
  // App feature toggles state
  const [appFeatures, setAppFeatures] = useState({
    agenda: true,
    exhibitors: true,
    floorPlan: true,
    networking: true,
    whatsOn: true,
    alerts: true,
    mySchedule: true
  });
  const handleFeatureToggle = (feature: keyof typeof appFeatures) => {
    setAppFeatures(prev => ({
      ...prev,
      [feature]: !prev[feature]
    }));
  };
  const renderFeedTab = () => <div className="space-y-6" data-id="element-2238">
      <div className="flex justify-between items-center" data-id="element-2239">
        <h3 className="text-lg font-bold text-gray-900" data-id="element-2240">What's On Feed</h3>
        <Button variant="primary" size="sm" data-id="element-2241">
          <PlusIcon className="w-4 h-4 mr-2" data-id="element-2242" />
          Add Item
        </Button>
      </div>

      <div className="grid gap-4" data-id="element-2243">
        {[1, 2, 3].map(item => <Card key={item} padding="md" className="flex justify-between items-center" data-id="element-2244">
            <div className="flex gap-4" data-id="element-2245">
              <div className="w-24 h-16 bg-gray-200 rounded-md" data-id="element-2246"></div>
              <div data-id="element-2247">
                <h4 className="font-bold text-gray-900" data-id="element-2248">
                  Feed Item Title {item}
                </h4>
                <p className="text-sm text-gray-500" data-id="element-2249">
                  Published • {new Date().toLocaleDateString()}
                </p>
              </div>
            </div>
            <div className="flex gap-2" data-id="element-2250">
              <Button variant="outline" size="sm" data-id="element-2251">
                <EditIcon className="w-4 h-4" data-id="element-2252" />
              </Button>
              <Button variant="outline" size="sm" className="text-red-500 hover:text-red-600" data-id="element-2253">
                <TrashIcon className="w-4 h-4" data-id="element-2254" />
              </Button>
            </div>
          </Card>)}
      </div>
    </div>;
  const renderSettingsTab = () => <div className="max-w-3xl space-y-6" data-id="element-2255">
      {/* General Settings */}
      <Card padding="lg" data-id="element-2256">
        <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-2257">
          General Settings
        </h3>
        <div className="space-y-4" data-id="element-2258">
          <div data-id="element-2259">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2260">
              {subEvent ? 'Sub-Event Name' : 'Event Name'}
            </label>
            <input type="text" defaultValue={subEvent ? subEvent.name : masterEvent.name} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-2261" />
          </div>
          {subEvent && <>
              <div data-id="element-2262">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2263">
                  Event Type
                </label>
                <select defaultValue={subEvent.type} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-2264">
                  <option value="B2B" data-id="element-2265">B2B (Business)</option>
                  <option value="B2C" data-id="element-2266">B2C (Consumer)</option>
                </select>
              </div>
              <div data-id="element-2267">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2268">
                  Theme Color
                </label>
                <div className="flex items-center gap-2" data-id="element-2269">
                  <input type="color" defaultValue={subEvent.color} className="w-10 h-10 rounded border border-gray-300 cursor-pointer" data-id="element-2270" />
                  <span className="text-sm text-gray-500" data-id="element-2271">
                    {subEvent.color}
                  </span>
                </div>
              </div>
            </>}
        </div>
      </Card>

      {/* App Features */}
      <Card padding="lg" data-id="element-2272">
        <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-2273">App Features</h3>
        <p className="text-sm text-gray-500 mb-6" data-id="element-2274">
          Control which features are available in the attendee app for this
          event
        </p>
        <div className="space-y-4" data-id="element-2275">
          {[{
          key: 'agenda',
          label: 'Agenda',
          description: 'Session schedule and bookings'
        }, {
          key: 'exhibitors',
          label: 'Exhibitors',
          description: 'Exhibitor directory and profiles'
        }, {
          key: 'floorPlan',
          label: 'Floor Plan',
          description: 'Interactive venue map'
        }, {
          key: 'networking',
          label: 'Networking',
          description: 'Meeting scheduling and connections'
        }, {
          key: 'whatsOn',
          label: "What's On",
          description: 'Event feed and announcements'
        }, {
          key: 'alerts',
          label: 'Alerts',
          description: 'Push notifications and updates'
        }, {
          key: 'mySchedule',
          label: 'My Schedule',
          description: 'Personal agenda and bookings'
        }].map(feature => <div key={feature.key} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0" data-id="element-2276">
              <div className="flex-1" data-id="element-2277">
                <div className="font-medium text-gray-900" data-id="element-2278">{feature.label}</div>
                <div className="text-sm text-gray-500" data-id="element-2279">
                  {feature.description}
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer" data-id="element-2280">
                <input type="checkbox" checked={appFeatures[feature.key as keyof typeof appFeatures]} onChange={() => handleFeatureToggle(feature.key as keyof typeof appFeatures)} className="sr-only peer" data-id="element-2281" />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600" data-id="element-2282"></div>
              </label>
            </div>)}
        </div>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end" data-id="element-2283">
        <Button variant="primary" data-id="element-2284">Save Changes</Button>
      </div>
    </div>;
  // Display name - use sub-event name if exists, otherwise master event name
  const displayName = subEvent ? subEvent.name : masterEvent.name;
  return <div className="space-y-6" data-id="element-2285">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 -mx-8 -mt-8 px-8 py-6 mb-8" data-id="element-2286">
        <div className="flex items-center gap-4" data-id="element-2287">
          {subEvent && <div className="w-10 h-10 rounded-lg flex items-center justify-center font-bold" style={{
          backgroundColor: subEvent.name === 'Dubai Muscle Show' ? '#000000' : subEvent.name === 'Dubai Active' ? '#c8e1d9' : subEvent.name === 'Dubai Active Industry' ? '#17313a' : subEvent.color,
          color: subEvent.name === 'Dubai Muscle Show' ? '#ffffff' : subEvent.name === 'Dubai Active' ? '#17313a' : subEvent.name === 'Dubai Active Industry' ? '#ffffff' : '#ffffff',
          fontSize: subEvent.name === 'Dubai Muscle Show' || subEvent.name === 'Dubai Active' || subEvent.name === 'Dubai Active Industry' ? '0.6rem' : '1.25rem'
        }} data-id="element-2288">
              {subEvent.name === 'Dubai Muscle Show' ? 'DMS' : subEvent.name === 'Dubai Active' ? 'DA' : subEvent.name === 'Dubai Active Industry' ? 'DAI' : subEvent.name.charAt(0)}
            </div>}
          <div data-id="element-2289">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-2290">{displayName}</h1>
            <p className="text-sm text-gray-500" data-id="element-2291">
              {subEvent ? `Part of ${masterEvent.name} • ${subEvent.dates}` : `${masterEvent.dates}`}
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="min-h-[500px]" data-id="element-2292">
        {activeTab === 'feed' && renderFeedTab()}
        {activeTab === 'settings' && renderSettingsTab()}
      </div>
    </div>;
}